import React, { useState, useCallback, useRef } from 'react';
import { clsx } from 'clsx';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  HiClock,
  HiEnvelope as HiMail,
  HiChatBubbleLeft as HiChat,
  HiPhone,
  HiGlobeAlt,
  HiCog,
  HiPaperClip,
  HiEye,
  HiArrowUturnLeft as HiReply,
  HiUserPlus as HiUserAdd,
  HiCheck
} from 'react-icons/hi2';
import { Avatar } from '@/components/Avatar';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { RichTextRenderer } from '@/components/editor/RichTextRenderer';
import {
  Ticket,
  TicketStatus,
  TicketPriority,
  TicketSource,
  getTicketStatusString,
  getTicketPriorityString
} from '@/types/api';
import type { TicketCardProps } from '@/types/ui';
import { useBulkSelection } from '@/contexts/BulkSelectionContext';
import { resolveDisplayTimestamp } from '@/utils/ticketTimestamp';

/**
 * Enhanced Ticket Card Component - Card View Layout
 * Based on Freshdesk interface analysis with improved UX
 */
export const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  selected: controlledSelected,
  onSelect,
  onClick,
  onStatusChange,
  onPriorityChange,
  onAssignmentChange,
  showQuickActions = true,
  compact = false,
  showAvatar = true,
  className,
  'data-testid': testId,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const displayTimestamp = resolveDisplayTimestamp(ticket.updatedAt, ticket.createdAt);

  // Use bulk selection context if available, otherwise fall back to controlled selected
  const bulkSelection = useBulkSelection();
  const selected = bulkSelection ? bulkSelection.isTicketSelected(ticket.id) : (controlledSelected || false);

  // Status color mapping based on Webcoda design system
  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.Open: return 'info';
      case TicketStatus.InProgress: return 'warning'; 
      case TicketStatus.OnHold: return 'secondary';
      case TicketStatus.Resolved: return 'success';
      case TicketStatus.Closed: return 'secondary';
      default: return 'secondary';
    }
  };

  // Priority color mapping with correct Freshdesk-inspired colors
  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case TicketPriority.Urgent: return 'error';
      case TicketPriority.High: return 'error';
      case TicketPriority.Medium: return 'warning';
      case TicketPriority.Low: return 'success';
      default: return 'secondary';
    }
  };

  // Source icon mapping
  const getSourceIcon = (source: TicketSource) => {
    const iconClass = "w-4 h-4 text-gray-400";
    switch (source) {
      case TicketSource.Email: return <HiMail className={iconClass} />;
      case TicketSource.Chat: return <HiChat className={iconClass} />;
      case TicketSource.Phone: return <HiPhone className={iconClass} />;
      case TicketSource.WebForm: return <HiGlobeAlt className={iconClass} />;
      case TicketSource.API: return <HiCog className={iconClass} />;
      default: return <HiGlobeAlt className={iconClass} />;
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return format(date, 'MMM d, yyyy');
  };

  // Enhanced card click handler with keyboard support
  const handleCardClick = useCallback((e: React.MouseEvent) => {
    // Don't trigger card click if clicking on interactive elements
    if (
      (e.target as HTMLElement).closest('button') ||
      (e.target as HTMLElement).closest('input') ||
      (e.target as HTMLElement).closest('[role="button"]')
    ) {
      return;
    }
    
    onClick?.(ticket);
  }, [onClick, ticket]);

  // Enhanced selection handler with bulk context integration
  const handleSelectToggle = useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    
    if (bulkSelection) {
      bulkSelection.toggleTicket(ticket.id);
    } else {
      onSelect?.(ticket.id, !selected);
    }
  }, [bulkSelection, onSelect, ticket.id, selected]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case ' ': // Spacebar - toggle selection
        e.preventDefault();
        handleSelectToggle(e);
        break;
      
      case 'Enter': // Enter - open ticket
        e.preventDefault();
        onClick?.(ticket);
        break;
      
      case 'r': // 'r' - mark as resolved
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          onStatusChange?.(ticket.id, TicketStatus.Resolved);
        }
        break;
      
      case 'a': // 'a' - open the ticket to assign it in the details properties
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          onClick?.(ticket);
        }
        break;
    }
  }, [handleSelectToggle, onClick, onStatusChange, ticket]);

  const handleStatusChange = (e: React.MouseEvent, newStatus: TicketStatus) => {
    e.stopPropagation();
    onStatusChange?.(ticket.id, newStatus);
  };

  const handlePriorityChange = (e: React.MouseEvent, newPriority: TicketPriority) => {
    e.stopPropagation();
    onPriorityChange?.(ticket.id, newPriority);
  };

  const cardClasses = clsx(
    // Base styles with modern 2025 design standards
    'bg-white',
    'border',
    'border-gray-200',
    'rounded-xl', // Increased border radius for modern look
    'transition-all',
    'duration-300', // Smoother transitions
    'hover:shadow-lg', // More pronounced hover shadow
    'hover:shadow-gray-100', // Subtle shadow color
    'focus-within:ring-2',
    'focus-within:ring-primary-500',
    'focus-within:ring-offset-2',

    // Selected state
    selected && 'ring-2 ring-primary-500 border-primary-300 bg-primary-50',

    // Fixed dimensions for consistency and professional layout
    'w-full',
    'max-w-sm', // Consistent maximum width
    compact ? 'h-80' : 'h-96', // Fixed height prevents content overflow
    'flex',
    'flex-col',
    'relative', // For absolute positioned hover actions
    'overflow-hidden', // Prevent content overflow

    // Enhanced spacing with modern 2025 standards
    compact ? 'p-6' : 'p-8',

    // Hover state with enhanced visual feedback
    'hover:border-gray-300',
    'hover:-translate-y-1', // Subtle lift effect
    'cursor-pointer',

    className
  );

  const quickActions = [
    {
      key: 'view',
      label: 'View Details',
      icon: <HiEye className="w-4 h-4" />,
      onClick: () => onClick?.(ticket)
    },
    {
      key: 'reply',
      label: 'Reply',
      icon: <HiReply className="w-4 h-4" />,
      onClick: () => onClick?.(ticket) // Navigate to ticket details to reply
    },
    {
      key: 'assign',
      label: 'Assign in Details',
      icon: <HiUserAdd className="w-4 h-4" />,
      // There is no inline assignment UI on the card; the real assignment
      // controls live in the details PropertiesPanel, so route there.
      onClick: () => onClick?.(ticket)
    },
    {
      key: 'resolve',
      label: 'Mark as Resolved',
      icon: <HiCheck className="w-4 h-4" />,
      onClick: (e: React.MouseEvent) => handleStatusChange(e, TicketStatus.Resolved),
      show: ticket.status !== TicketStatus.Resolved && ticket.status !== TicketStatus.Closed
    }
  ];

  return (
    <div
      ref={cardRef}
      className={cardClasses}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={testId}
      role="article"
      tabIndex={0}
      aria-label={`Ticket ${ticket.id}: ${ticket.subject}. ${selected ? 'Selected' : 'Not selected'}. Press space to toggle selection, enter to open.`}
      aria-selected={selected}
      {...props}
    >
      {/* Main Content - Flexible height */}
      <div className="flex-1 flex flex-col">
        {/* Card Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-start space-x-4 flex-1 min-w-0">
            {/* Selection Checkbox */}
            <div className="flex-shrink-0 mt-1">
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => handleSelectToggle(e)}
                className={clsx(
                  "w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 transition-all",
                  selected && "bg-primary-600 border-primary-600"
                )}
                aria-label={`Select ticket ${ticket.id}`}
                tabIndex={-1} // Card handles keyboard navigation
              />
            </div>

            {/* Ticket Info - Full width for title and content */}
            <div className="flex-1 min-w-0">
              {/* Ticket ID - Subtle row at top */}
              <div className="mb-2">
                <span className="text-xs text-gray-400 font-medium">
                  #{ticket.ticketNumber || ticket.id}
                </span>
              </div>

              {/* Source, date, and attachments - Clean secondary row */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center space-x-2">
                  {getSourceIcon(ticket.source)}
                  <span className="text-xs text-gray-500">
                    {formatDate(ticket.createdAt)}
                  </span>
                </div>
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <HiPaperClip className="w-3 h-3 text-gray-400" title="Has attachments" />
                )}
              </div>

              {/* Subject with smart truncation and tooltip - Now has full width */}
              <h3
                className={clsx(
                  'font-medium text-gray-900 leading-relaxed mb-5',
                  'line-clamp-2', // CSS line clamping for smart word breaks
                  'break-words', // Allow breaking long words
                  'hyphens-auto', // Auto hyphenation for better text flow
                  compact ? 'text-sm' : 'text-base'
                )}
                title={ticket.subject} // Tooltip for full text
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word'
                }}
              >
                {ticket.subject}
              </h3>

              {/* Customer info with enhanced truncation */}
              <div className="flex items-center space-x-3 mt-4 min-w-0">
                <span
                  className={clsx(
                    'text-gray-600 font-medium flex-shrink-0 max-w-32',
                    'truncate',
                    compact ? 'text-xs' : 'text-sm'
                  )}
                  title={ticket.requester.fullName}
                >
                  {ticket.requester.fullName}
                </span>
                {ticket.company && (
                  <>
                    <span className="text-gray-300 text-sm flex-shrink-0">•</span>
                    <span
                      className={clsx(
                        'text-gray-500 truncate min-w-0 flex-1',
                        compact ? 'text-xs' : 'text-sm'
                      )}
                      title={ticket.company.name}
                    >
                      {ticket.company.name}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Customer Avatar - Moved to right side */}
          {showAvatar && (
            <Avatar
              name={ticket.requester.fullName}
              size={compact ? 'sm' : 'md'}
              src={ticket.requester.profileImageUrl}
              className="flex-shrink-0 ml-4"
            />
          )}

          {/* Enhanced Quick Actions - Always positioned, visibility controlled */}
          {showQuickActions && (
            <div className={clsx(
              'absolute top-4 right-4 flex items-center space-x-2',
              'transition-all duration-300 ease-in-out',
              'bg-white/95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200',
              'p-2',
              isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
            )}>
              {quickActions.filter(action => action.show !== false).map(action => (
                <Button
                  key={action.key}
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick(e);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors duration-200"
                  title={action.label}
                >
                  {action.icon}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Card Body - Enhanced Description Preview */}
        {!compact && (
          <div className="flex-1 mb-6 min-h-0">
            <div className="text-sm text-gray-600 leading-relaxed overflow-hidden">
              <div
                className="line-clamp-3"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  hyphens: 'auto'
                }}
                title={ticket.description} // Tooltip for full description
              >
                <RichTextRenderer
                  content={ticket.description}
                  htmlContent={ticket.descriptionHtml}
                  className="text-inherit"
                  maxLength={180}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Card Footer - Enhanced sticky footer */}
      <div className="mt-auto pt-6 border-t border-gray-100 space-y-4">
        {/* Badges Row with improved spacing */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Priority Badge */}
          <Badge
            variant={getPriorityColor(ticket.priority)}
            size={compact ? 'sm' : 'md'}
            className="px-3 py-1.5 font-medium"
          >
            {getTicketPriorityString(ticket.priority)}
          </Badge>

          {/* Status Badge */}
          <Badge
            variant={getStatusColor(ticket.status)}
            size={compact ? 'sm' : 'md'}
            className="px-3 py-1.5 font-medium"
          >
            {getTicketStatusString(ticket.status)}
          </Badge>
        </div>

        {/* Assignment and Time Row with better layout */}
        <div className="flex items-center justify-between min-w-0">
          {/* Assignment Info with enhanced layout */}
          <div className="flex items-center min-w-0 flex-1">
            {ticket.assignedTo ? (
              <div className="flex items-center space-x-2 min-w-0">
                <Avatar
                  name={ticket.assignedTo.fullName}
                  size="xs"
                  src={ticket.assignedTo.profileImageUrl}
                  className="flex-shrink-0"
                />
                <span
                  className="text-xs text-gray-600 font-medium truncate"
                  title={ticket.assignedTo.fullName}
                >
                  {ticket.assignedTo.fullName.split(' ')[0]}
                </span>
              </div>
            ) : (
              <span className="text-xs text-gray-400 font-medium">Unassigned</span>
            )}
          </div>

          {/* Last Updated with enhanced styling */}
          <div className="flex items-center text-xs text-gray-500 flex-shrink-0 ml-3">
            <HiClock className="w-3 h-3 mr-1.5" />
            {displayTimestamp ? (
              <span title={format(new Date(displayTimestamp), 'PPp')}>
                {formatDistanceToNow(new Date(displayTimestamp), { addSuffix: true })}
              </span>
            ) : <span>Not available</span>}
          </div>
        </div>
      </div>

      {/* Tags - Show if not compact */}
      {!compact && ticket.tags && ticket.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-100">
          {ticket.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
            >
              {tag}
            </span>
          ))}
          {ticket.tags.length > 3 && (
            <span className="text-xs text-gray-400">
              +{ticket.tags.length - 3} more
            </span>
          )}
        </div>
      )}
    </div>
  );
};
